import { ImagesIcon } from "lucide-react";
import { useState } from "react";
import type { SessionDetail } from "../../../../shared/api.ts";
import { EmptyState } from "@/components/common/states.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { domainOf } from "@/lib/utils.ts";
import { Lightbox, type LightboxImage } from "./lightbox.tsx";

/** One image of the session, with where it came from. */
export type SessionImage = LightboxImage & { origin: "picture" | "upload" | "source" };

const ORIGINS: Array<SessionImage["origin"]> = ["picture", "upload", "source"];

/**
 * Every image of the session, each once: the pictures of the good, then the images the user attached,
 * then the image documents that sources stored. A document already shown earlier in the list is skipped.
 */
export function collectImages(detail: SessionDetail): SessionImage[] {
  const images: SessionImage[] = [];
  const seen = new Set<string>();
  const add = (image: SessionImage) => {
    if (seen.has(image.id) || seen.has(image.url)) return;
    seen.add(image.id);
    seen.add(image.url);
    images.push(image);
  };
  for (const picture of detail.pictures) add({ ...picture, origin: "picture" });
  for (const message of detail.messages) {
    if (message.role !== "user") continue;
    for (const attachment of message.attachments) {
      if (attachment.kind === "image") add({ id: attachment.id, url: attachment.url, caption: attachment.name, pageUrl: null, imageUrl: null, origin: "upload" });
    }
  }
  for (const source of detail.sources) {
    const doc = source.document;
    if (doc?.kind !== "image" || !doc.rawUrl) continue;
    const pageUrl = source.url && /^https?:\/\//i.test(source.url) ? source.url : null;
    // The address it was read from is already pageUrl, so there is no other file address to link.
    add({ id: doc.id, url: doc.rawUrl, caption: source.title || domainOf(source.url) || "", pageUrl, imageUrl: null, origin: "source" });
  }
  return images;
}

/** The images of the session as thumbnail grids, by origin. A thumbnail opens the lightbox. */
export function ImagesTab({ detail }: { detail: SessionDetail }) {
  const { t } = useI18n();
  const [lightbox, setLightbox] = useState<number | null>(null);
  const images = collectImages(detail);
  if (images.length === 0) {
    return <EmptyState icon={ImagesIcon} text={t.images.empty} className="py-14" />;
  }
  const titles: Record<SessionImage["origin"], string> = { picture: t.images.pictures, upload: t.images.uploads, source: t.images.sources };
  return (
    <div className="pb-4">
      {ORIGINS.map((origin) => {
        const items = images.flatMap((image, index) => (image.origin === origin ? [{ image, index }] : []));
        if (items.length === 0) return null;
        return (
          <section key={origin} aria-label={titles[origin]}>
            <h3 className="border-b px-4 py-2 text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase">{titles[origin]}</h3>
            <ul className="grid grid-cols-3 gap-x-2 gap-y-3 px-4 py-3">
              {items.map(({ image, index }) => {
                const caption = image.caption || t.panel.pictureAlt(index + 1);
                return (
                  <li key={image.id} className="min-w-0">
                    <button
                      type="button"
                      onClick={() => setLightbox(index)}
                      aria-label={`${t.panel.pictureOpen}: ${caption}`}
                      className="group flex w-full flex-col gap-1.5 text-left outline-none"
                    >
                      <span className="block aspect-square overflow-hidden rounded-lg border bg-muted transition-[border-color,box-shadow] group-hover:border-primary/40 group-focus-visible:ring-[3px] group-focus-visible:ring-ring/35">
                        <img src={image.url} alt="" loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                      </span>
                      <span className="line-clamp-2 text-[11.5px] leading-snug break-words text-muted-foreground" title={caption}>
                        {caption}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
      <Lightbox pictures={images} index={lightbox} onIndexChange={setLightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
